import React from "react";
import { sentenceCase } from "@/lib/sentenceCase";

/**
 * HierarchyHeaders — renders the Chapter / Article / Subarticle banners
 * that are injected before the first paragraph of each level inside the
 * Catechism reading view. Up to three banners may stack on a single
 * paragraph when (e.g.) the first paragraph of a Chapter is also the
 * first paragraph of its Article and its first Subarticle. Ordering is
 * fixed: Chapter (largest) → Article → Subarticle.
 *
 * Visual contract (matches the user's spec):
 *  • Chapter  → <h2> bold, centered, generous top margin
 *  • Article  → <h3> bold, brand-blue, paragraph range in parens
 *  • Subart.  → <h4> bold italic, 16 px left indent
 *
 * The wrapping <li> is unstyled and aria-hidden=false so screen readers
 * announce each level as the user scrolls through the linear list.
 */
export default function HierarchyHeaders({ headers }) {
    return (
        <li
            className="list-none"
            aria-hidden="false"
            data-testid="ccc-hierarchy-headers"
        >
            {headers.chapter && (
                <h2
                    data-testid="ccc-h2-chapter"
                    className="heading-serif text-2xl sm:text-3xl font-bold text-center text-stone900 mt-16 mb-6 leading-tight"
                >
                    {sentenceCase(headers.chapter.title)}
                </h2>
            )}
            {headers.article && (
                <h3
                    data-testid="ccc-h3-article"
                    className="heading-serif text-xl sm:text-2xl font-bold text-sangre mt-10 mb-4 leading-snug"
                >
                    {headers.article.label
                        ? <span>{headers.article.label}: </span>
                        : null}
                    {sentenceCase(headers.article.title)}
                    {headers.article.start && (
                        <span className="ui-sans font-normal text-stoneMuted tabular-nums ml-2">
                            ({headers.article.start}
                            {headers.article.end && headers.article.end !== headers.article.start
                                ? `–${headers.article.end}` : ""})
                        </span>
                    )}
                </h3>
            )}
            {headers.subarticle && (
                <h4
                    data-testid="ccc-h4-subarticle"
                    className="reading-serif italic font-bold text-base sm:text-lg text-stone900 mt-7 mb-3 ml-4 leading-snug"
                >
                    <span className="text-sangre mr-2">{headers.subarticle.label}.</span>
                    {sentenceCase(headers.subarticle.title)}
                </h4>
            )}
        </li>
    );
}
